// src/Components/Common/GeneralSettings.jsx - Enhanced for Advanced Settings
import React, { useState } from "react";
import { Card, CardBody, CardHeader, Row, Col, Button, ButtonGroup } from "reactstrap";
import FormField from "./FormField";

const GeneralSettings = ({
  settings,
  onUpdateSetting,
  onReset,
  config,
  title = "General Settings",
  showReset = true,
  loading = false,
  selectedFiles = [],
  onSmartAdjustment = null,
  mode = "single",
  analysisMode = null,
  onSetAnalysisMode = null,
  analysisModeOptions = [
    { value: "single", label: "Single" },
    { value: "comparison", label: "Comparison" },
  ]
}) => {
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);

  const renderFields = (fields) => {
    return fields
      .filter((field) => {
        // Filter fields based on mode
        if (field.mode && field.mode !== mode) {
          return false;
        }
        return true;
      })
      .map((field) => (
        <Col key={field.id} md={field.width || 6} lg={field.width || 6}>
          <FormField
            id={field.id}
            label={field.label}
            type={field.type}
            value={settings[field.id]}
            onChange={(value) => {
              onUpdateSetting(field.id, value);
              if (field.triggersSmartAdjustment && onSmartAdjustment) {
                onSmartAdjustment(field.id, value, selectedFiles);
              }
            }}
            placeholder={field.placeholder}
            tooltip={field.tooltip}
            options={field.options}
            min={field.min}
            max={field.max}
            step={field.step}
            className={field.className}
            width={field.width}
            disabled={field.disabled || loading}
            // required={field.required}
            rows={field.rows}
          />
        </Col>
      ));
  };

  // ✅ Separate regular and advanced fields
  const regularFields = config.sections[0].fields.filter(
    (field) => !field.advanced && (!field.mode || field.mode === mode)
  );

  const advancedFields = config.sections[0].fields.filter(
    (field) => field.advanced && (!field.mode || field.mode === mode)
  );

  return (
    <>
      <Card
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: "4px",
          height: "100%",
          minHeight: "200px",
        }}
      >
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0" style={{ color: "#1a6d51" }}>
            <i className="ri-settings-3-line me-2"></i>
            {title}
          </h5>

          <div className="d-flex gap-2">
            {/* ✅ Advanced Settings Button - Only show if there are advanced fields */}
            {advancedFields.length > 0 && (
              <Button
                color="link"
                size="sm"
                onClick={() => setIsAdvancedModalOpen(true)}
                style={{
                  color: "#33a849",
                  fontSize: "14px",
                  fontWeight: "500",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  textDecoration: "none",
                }}
              >
                <i
                  className="ri-settings-3-line me-1"
                  style={{ fontSize: "14px" }}
                />
                Advanced Settings
              </Button>
            )}

            {/* Reset Button */}
            {showReset && (
              <Button
                color="outline-secondary"
                size="sm"
                onClick={onReset}
                disabled={loading}
              >
                <i className="ri-refresh-line me-1"></i>
                Reset
              </Button>
            )}
          </div>
        </CardHeader>

        <CardBody style={{ padding: "20px" }}>
            {/* Analysis Mode Toggle - shown when props are provided */}
            {analysisMode && onSetAnalysisMode && (
              
              <div className="d-flex align-items-center mb-5">
                <span className="fs-15 me-2">Analysis type:</span>
                <ButtonGroup>
                  {analysisModeOptions.map(({ value, label }) => (
                    <Button key={value} size="sm" onClick={() => onSetAnalysisMode(value)}
                      style={{
                        backgroundColor: analysisMode === value ? "#2F9E37" : "transparent",
                        borderColor: "#2F9E37",
                        color: analysisMode === value ? "white" : "#2F9E37",
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </ButtonGroup>
              </div>
            )}
          {config.sections.map((section, index) => (
            <div key={index}>
              {section.title && (
                <h6
                  className="mb-3 text-muted"
                  style={{ fontSize: "13px", fontWeight: "600" }}
                >
                  {section.title}
                </h6>
              )}
              <Row className={section.className || ""}>
                {renderFields(regularFields)}
              </Row>
              {index < config.sections.length - 1 && section.title && (
                <hr className="my-4" />
              )}
            </div>
          ))}
        </CardBody>
      </Card>

      {/* ✅ Advanced Settings Modal */}
      {isAdvancedModalOpen && advancedFields.length > 0 && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setIsAdvancedModalOpen(false)}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "8px",
                padding: "24px",
                minWidth: "500px",
                maxWidth: "600px",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
                border: "1px solid #e5e7eb",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5
                  className="mb-0"
                  style={{
                    color: "#33a849",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                >
                  <i className="ri-settings-3-line me-2"></i>
                  Advanced Settings
                </h5>
                <Button
                  color="link"
                  className="p-0"
                  onClick={() => setIsAdvancedModalOpen(false)}
                  style={{
                    color: "#6b7280",
                    fontSize: "20px",
                    textDecoration: "none",
                  }}
                >
                  <i className="ri-close-line"></i>
                </Button>
              </div>

              {/* Modal Body - Advanced Fields */}
              <div>
                <Row>{renderFields(advancedFields)}</Row>
              </div>

              {/* Modal Footer */}
              <div className="d-flex justify-content-end gap-2 mt-4">
                <Button
                  onClick={() => setIsAdvancedModalOpen(false)}
                  style={{
                    backgroundColor: "#33a849",
                    borderColor: "#33a849",
                    color: "white",
                    fontSize: "14px",
                    padding: "6px 16px",
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GeneralSettings;
